import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Box,
  MenuItem,
  Divider,
  Typography,
} from '@mui/material';
import { supabase } from '../lib/supabase';
import { AutoTopUpConfigModal, Rule } from './AutoTopUpConfigModal';

// Define a suggested list of use case types based on PRD
const USE_CASE_TYPES = [
    'Fleet Tracking',
    'CCTV',
    'Telemetry Sensors',
    'Vibration Sensor',
    'Load Cell Sensor',
    'Torque Sensor',
    'Acoustic Sensor',
    'Motion Sensor',
    'Thermal Camera',
    'Pressure Sensor',
    'Position Sensor',
    'Accelerometer Tilt Sensor',
    'Telemetry Sensor Systems',
    'Reactive Polymer Based Sensor',
    'Other', // Add an 'Other' option
];

// Type for the cluster data being edited/created
export type ClusterFormData = {
  id?: string; // Present when editing
  organization_id?: string; // Needed for insert/update context
  name: string;
  use_case_type: string;
  is_critical: boolean;
  auto_topup_enabled?: boolean; // Make optional for safety, handle default
  auto_topup_config?: Rule[] | null; // Use the imported Rule type
  default_sim_status?: string; // Add this field
  usage_limit_value?: number | null; // Monthly usage limit in ZAR
};

type ClusterFormModalProps = {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  cluster?: ClusterFormData | null; // Pass existing cluster data for editing
  organizationId: string; // Always require the org context
};

export function ClusterFormModal({
  open,
  onClose,
  cluster,
  organizationId,
}: ClusterFormModalProps) {
  // Define initial state structure including new fields
  const initialFormData: ClusterFormData = {
    name: '',
    use_case_type: USE_CASE_TYPES[0],
    is_critical: false,
    auto_topup_enabled: false,
    auto_topup_config: [],
    organization_id: organizationId, // Set org ID for new
    default_sim_status: 'active', // Default to active
    usage_limit_value: null, // No limit by default
  };

  const [formData, setFormData] = useState<ClusterFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configModalOpen, setConfigModalOpen] = useState(false); // State for nested modal

  const isEditMode = Boolean(cluster?.id);

  useEffect(() => {
    if (open) { // Reset/populate form only when modal opens
        if (cluster && isEditMode) {
            setFormData({
                id: cluster.id,
                organization_id: cluster.organization_id,
                name: cluster.name || '',
                use_case_type: cluster.use_case_type || USE_CASE_TYPES[0],
                is_critical: cluster.is_critical || false,
                auto_topup_enabled: cluster.auto_topup_enabled ?? false, // Use ?? for default
                auto_topup_config: cluster.auto_topup_config ?? [], // Use ?? for default
                default_sim_status: cluster.default_sim_status ?? 'active', // Populate from cluster data
                usage_limit_value: cluster.usage_limit_value ?? null, // Populate usage limit
            });
        } else {
            // Reset form for creating, ensuring orgId is set
            setFormData({ ...initialFormData, organization_id: organizationId });
        }
        setError(null);
        setLoading(false);
    }
  }, [cluster, isEditMode, open, organizationId]); // Removed initialFormData dependency

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    // Special handling for usage limit (allow empty string to reset to null)
    if (name === 'usage_limit_value') {
      const numValue = value === '' ? null : Number(value);
      setFormData((prev) => ({
        ...prev,
        [name]: numValue,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' || type === 'switch' ? checked : value, // Handle switch type
    }));
  };

  // Handler for closing the nested AutoTopUpConfigModal
  const handleConfigModalClose = (updatedConfig?: { enabled: boolean; rules: Rule[] } | null) => {
    setConfigModalOpen(false);
    if (updatedConfig) {
        console.log('[ClusterForm] AutoTopUpConfigModal closed with:', updatedConfig);
        // Update formData based on the nested modal's output
        setFormData(prev => ({
            ...prev,
            auto_topup_enabled: updatedConfig.enabled,
            auto_topup_config: updatedConfig.rules,
        }));
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.use_case_type) {
        setError('Cluster Name and Use Case Type are required.');
        return;
    }
    setLoading(true);
    setError(null);
    try {
        const dataToSave = {
            name: formData.name,
            use_case_type: formData.use_case_type,
            is_critical: formData.is_critical,
            // Include auto top-up fields
            auto_topup_enabled: formData.auto_topup_enabled,
            // Set config to null if disabled, otherwise use the stored config
            auto_topup_config: formData.auto_topup_enabled ? formData.auto_topup_config : null,
            default_sim_status: formData.default_sim_status ?? 'active', // Include in save data
            usage_limit_value: formData.usage_limit_value, // Include usage limit
        };

        let dbError;
        let savedClusterId = formData.id; // Use existing ID if editing

        if (isEditMode) {
            const { error } = await supabase
                .from('clusters')
                .update({ ...dataToSave, updated_at: new Date().toISOString() })
                .eq('id', formData.id);
            dbError = error;
        } else {
            // Insert and select the new ID back
            const { data: newData, error } = await supabase
                .from('clusters')
                .insert({ ...dataToSave, organization_id: organizationId })
                .select('id') 
                .single();
            dbError = error;
            if (!dbError && newData) {
                savedClusterId = newData.id;
            }
        }

        if (dbError) throw dbError;
        
        // --- Call RPC to apply default status --- 
        if (savedClusterId) {
            console.log(`Calling RPC update_cluster_sim_status for cluster ${savedClusterId} with status ${dataToSave.default_sim_status}`);
            const { data: rpcData, error: rpcError } = await supabase.rpc('update_cluster_sim_status', {
                target_cluster_id: savedClusterId,
                new_default_status: dataToSave.default_sim_status
            });
            if (rpcError) {
                 console.error("RPC Error applying default status:", rpcError);
                 // Decide how to handle - maybe show a warning but still close?
                 setError("Cluster saved, but failed to apply default status to SIMs: " + rpcError.message);
                 // Don't throw here, allow modal to close
            } else if (rpcData && !rpcData.success) {
                 console.error("RPC Failed applying default status:", rpcData.error);
                 setError("Cluster saved, but failed to apply default status to SIMs: " + rpcData.error);
            } else {
                 console.log("RPC Success applying default status:", rpcData);
            }
        } else {
             console.warn("Could not get cluster ID, skipping RPC call.");
        }
        // --- End RPC Call --- 

        onClose(true); // Close and signal refresh
    } catch (err: any) {
        console.error('Error saving cluster:', err);
        setError(err.message || 'Failed to save cluster.');
    } finally {
        setLoading(false);
    }
  };

  return (
    <>
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{isEditMode ? 'Edit Cluster' : 'Create New Cluster'}</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box component="form" noValidate sx={{ mt: 1 }}>
              <TextField
                required
                margin="dense"
                id="name"
                name="name"
                label="Cluster Name"
                type="text"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                variant="outlined"
                autoFocus
              />
              <TextField
                required
                select
                margin="dense"
                id="use_case_type"
                name="use_case_type"
                label="Use Case Type"
                fullWidth
                value={formData.use_case_type}
                onChange={handleChange}
                variant="outlined"
              >
                {USE_CASE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_critical}
                    onChange={handleChange}
                    name="is_critical"
                  />
                }
                label="Is Critical?"
                sx={{ mt: 1 }}
              />

              {/* --- Default SIM Status --- */} 
              <Divider sx={{ my: 2 }} />
               <Typography variant="subtitle1" gutterBottom>Default SIM Status</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Set the default status for SIMs in this cluster. Individual SIMs can override this setting.
              </Typography>
               <TextField
                    select
                    margin="dense"
                    id="default_sim_status"
                    name="default_sim_status"
                    label="Default Status for SIMs"
                    fullWidth
                    value={formData.default_sim_status ?? 'active'}
                    onChange={handleChange}
                    variant="outlined"
                >
                    <MenuItem value="active">Active (Default)</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
               {/* --- End Default SIM Status --- */}

              {/* Auto Top-Up Section */} 
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>Cluster Auto Top-Up</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Define default auto top-up rules for SIMs assigned to this cluster. 
                  These rules apply unless overridden by SIM-specific settings.
              </Typography>
               <FormControlLabel
                   control={
                       <Switch
                           checked={formData.auto_topup_enabled ?? false}
                           onChange={handleChange}
                           name="auto_topup_enabled"
                       />
                   }
                   label="Enable Cluster Auto Top-Up"
                   sx={{ mt: 1 }}
               />
               {formData.auto_topup_enabled && (
                   <Button 
                       variant="outlined" 
                       onClick={() => setConfigModalOpen(true)} 
                       sx={{ mt: 1, ml: 2 }} // Adjust margin as needed
                   >
                       Configure Rules ({formData.auto_topup_config?.length || 0} rules)
                   </Button>
               )}

              {/* Monthly Usage Limit Section */}
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>Monthly Usage Limit</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Define the maximum monthly auto top-up spend in ZAR for this cluster.
              </Typography>
              <TextField
                type="number"
                margin="dense"
                id="usage_limit_value"
                name="usage_limit_value"
                label="Maximum Monthly Usage Limit (ZAR)"
                fullWidth
                value={formData.usage_limit_value === null ? '' : formData.usage_limit_value}
                onChange={handleChange}
                variant="outlined"
                InputProps={{
                  inputProps: { min: 0, step: 10 }
                }}
                helperText="Maximum monthly auto top-up spend in ZAR. Leave empty for no limit."
              />

            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onClose(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Nested AutoTopUpConfigModal */}
        {/* Pass cluster ID or a placeholder if needed by modal, otherwise maybe not required */}
        <AutoTopUpConfigModal
            open={configModalOpen}
            onClose={handleConfigModalClose}
            simId={formData.id || 'cluster-level'} // Use cluster ID or placeholder
            initialConfig={formData.auto_topup_config ?? []}
            initialEnabled={formData.auto_topup_enabled ?? false}
            isClusterLevel={true} // Add a prop to indicate context
        />
    </>
  );
} 